/**
 * FleetManager V3 - Main Application
 * Adaptive UI based on user profile
 * Now uses real data from APIs and connects to V1 components
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ProfileProvider } from './context/ProfileContext.jsx';
import { useProfile } from './hooks/useProfile.js';
import { AuthProvider, useAuth } from './context/AuthContext.jsx';
import { isViewAllowed, getHomeView } from './utils/viewAccess.js';
import { captureScanParam, takePendingScan } from '../utils/scanParam.js';
import { NavigationProvider, useNavigation } from './context/NavigationContext.jsx';
import { SearchProvider } from './context/SearchContext.jsx';
import { PipelineProvider, usePipeline } from './context/PipelineContext.jsx';
import { ThemeProvider } from './context/ThemeContext.jsx';
import { Layout } from './components/layout/index.js';
import { Dashboard } from './components/dashboard/Dashboard.jsx';
import { PlaceholderPage } from './pages/PlaceholderPage.jsx';
import { ProfileAdminView } from './components/admin/ProfileAdminView.jsx';
import SuperAdminDashboard from './pages/SuperAdminDashboard.jsx';
import LoginPage from './pages/LoginPage.jsx';
import { vehicleAPI, driverAPI, claimAPI, onboardingAPI, workingSiteAPI } from '../api.js';
import { Loader2, ArrowLeft, ListChecks } from 'lucide-react';

// UX Enhancement providers
import { ToastProvider, useToast } from '../components/common/Toast.jsx';
import { KeyboardShortcutsProvider, useShortcut } from '../components/common/KeyboardShortcuts.jsx';
import ErrorBoundary from '../components/common/ErrorBoundary.jsx';

// Onboarding Wizard
import { OnboardingWizard } from './components/onboarding/index.js';

// Welcome Modal and Feature Tour
import { WelcomeModal } from './components/welcome/index.js';
import { TourProvider, useTour } from './components/tour/index.js';
import { welcomeAPI } from '../api/index.js';

// Visual Help System
import { VisualHelpProvider, useVisualHelp } from './components/help/VisualHelpProvider.jsx';
import { KeyboardShortcutsModal } from './components/help/KeyboardShortcutsModal.jsx';
import { FloatingHelpPanel } from './components/help/FloatingHelpPanel.jsx';

// Guided Experience System (step-by-step coaching)
import { GuidedExperienceProvider, useGuidedExperience } from './components/help/GuidedExperienceProvider.jsx';
import { GuidedExperienceOverlay } from './components/help/GuidedExperienceOverlay.jsx';

// Onboarding Checklist Card (new user quick start)
import { OnboardingChecklistCard } from './components/help/OnboardingChecklistCard.jsx';

// Import V1 components for full functionality
import VehiclesView from '../components/vehicles/VehiclesView.jsx';
import DriversView from '../components/drivers/DriversView.jsx';
import MaintenanceView from '../components/maintenance/MaintenanceView.jsx';
import FuelView from '../components/fuel/FuelView.jsx';
import InspectionsView from '../components/inspections/InspectionsView.jsx';
import InsuranceUserView from '../components/insurance/InsuranceUserView.jsx';
import InsuranceConfigView from '../components/insurance/InsuranceConfigView.jsx';
import ToolsView from '../components/tools/ToolsView.jsx';
import GDPRComplianceView from '../components/gdpr/GDPRComplianceView.jsx';
import CostDashboardView from '../components/costs/CostDashboardView.jsx';
import AssignmentsView from '../components/assignments/AssignmentsView.jsx';
import AppRadarView from '../components/radar/AppRadarView.jsx';
import AdminView from '../components/admin/AdminView.jsx';
import FeatureMonitorTab from '../components/admin/FeatureMonitorTab.jsx';
import EmailTemplatesView from '../components/emails/EmailTemplatesView.jsx';
import DataExportView from '../components/admin/DataExportView.jsx';
import FournisseursView from '../components/fournisseurs/FournisseursView.jsx';
import ReportsView from '../components/reports/ReportsView.jsx';
import ReportArchiveView from '../components/reports/ReportArchiveView.jsx';
import DocumentsView from '../components/documents/DocumentsView.jsx';
import FinesView from '../components/fines/FinesView.jsx';
import CampaignsView from '../components/campaigns/CampaignsView.jsx';
import FleetCockpitView from '../components/cockpit/FleetCockpitView.jsx';
import ShareList from '../components/shares/ShareList.jsx';
import RiskProfilesView from '../components/drivers/RiskProfilesView.jsx';
import PipelineHomeV2 from '../components/pipeline/PipelineHomeV2.jsx';
import SitesView from '../components/sites/SitesView.jsx';
import PoolExceptionsView from '../components/pool/PoolExceptionsView.jsx';
import PoolDriverView from '../components/pool/PoolDriverView.jsx';
import CalendarView from '../components/calendar/CalendarView.jsx';
import AutoResolveToast from '../components/common/AutoResolveToast.jsx';

// Driver Mono View (self-service for drivers)
import DriverMonoView from './components/driver-mono/DriverMonoView.jsx';
import DriverLayout from './components/driver-mono/DriverLayout.jsx';

// Mission Manager View (CRUD for managers)
import MissionManagerView from './components/missions/MissionManagerView.jsx';

// Teams View (equipes - operational team management)
import TeamsView from '../components/teams/TeamsView.jsx';

// Archive View for offboarded customers
import ArchiveView from '../components/archive/ArchiveView.jsx';

// Fiscal Dashboard (ex-TVS / CO2 / ZFE)
import FiscalDashboardView from '../components/fiscal/FiscalDashboardView.jsx';

// Budget Planning & Control
import BudgetView from '../components/budget/BudgetView.jsx';

// LAZY-LOADED COMPONENTS
// Telematics module is lazy-loaded to reduce main bundle size (~50-100KB)
// See: TELEMATICS_QUICK_FIX_PLAN.md (Day 4)
const TelematicsView = React.lazy(() => import('../components/telematics/TelematicsView.jsx'));
const TelematicsMonitorView = React.lazy(() => import('../components/admin/TelematicsMonitorView.jsx'));

// BillingDashboard lazy-loaded to avoid loading Stripe.js on login page
const BillingDashboard = React.lazy(() => import('./pages/BillingDashboard.jsx'));

// E-Invoices (Facture Électronique) — module sous feature flag tenant
// `feature_e_invoices_enabled` (S2 : aucun tenant prod activé, dev/tests uniquement).
const EInvoicesView = React.lazy(() => import('./views/EInvoicesView.jsx'));

// QR pare-brise : `?scan=<jeton>` est lu UNE fois au chargement, avant même la connexion,
// puis retiré de l'adresse. Il est consommé dans AppContent, une fois l'utilisateur connecté.
// (Après les imports : `import/first` refuse une instruction au milieu.)
captureScanParam();

// View labels for placeholder pages (only used for non-implemented views)
const VIEW_LABELS = {
  documents: 'Documents',
  analytics: 'Analytics'
};

// Wrapper for V1 components - now just adds padding since layout is already light
function V1Wrapper({ children }) {
  return (
    <div className="min-h-full p-4">
      <ErrorBoundary>
        {children}
      </ErrorBoundary>
    </div>
  );
}

function AppContent() {
  const { user, tenant, isSuperuser, hasFeature } = useAuth();
  const { profile } = useProfile();
  const { t } = useTranslation();
  const toast = useToast();
  const isAdmin = user?.role === 'admin' || isSuperuser;
  const { pipelineEnabled, loading: pipelineLoading } = usePipeline();
  const [activeView, setActiveView] = useState(null); // Will be set after pipeline check
  const [viewParams, setViewParams] = useState({}); // Parameters for views (e.g., auto-select claim/maintenance)
  const [counts, setCounts] = useState({ vehicles: null, drivers: null, claims: null, sites: null });
  const [criticalAlerts, setCriticalAlerts] = useState(null);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [onboardingChecked, setOnboardingChecked] = useState(false);
  const [showWelcome, setShowWelcome] = useState(false);
  const [welcomeChecked, setWelcomeChecked] = useState(false);
  const [driverTab, setDriverTab] = useState('home'); // Active tab for driver layout

  // Track previous pipeline state to detect changes
  const prevPipelineEnabled = React.useRef(null);

  // La vue d'accueil de CE profil — une seule règle pour le login, Ctrl+H et le renvoi
  // d'une vue refusée (frontend/src/v3/utils/viewAccess.js).
  const homeView = useMemo(
    () => getHomeView(profile, { pipelineEnabled, isAdmin, isSuperuser, hasFeature }),
    [profile, pipelineEnabled, isAdmin, isSuperuser, hasFeature]
  );

  // Set view based on pipeline mode - both initial and on toggle
  useEffect(() => {
    if (pipelineLoading) {
      return;
    }

    // Initial load — l'accueil dépend du PROFIL (getHomeView) : le conducteur n'a pas
    // de tableau de bord et atterrit sur « Mon véhicule » (2026-09-14). Tant que le
    // profil n'est pas chargé, on attend : choisir sans lui ferait clignoter le dashboard.
    if (activeView === null) {
      if (!profile) return;
      setActiveView(homeView);
      prevPipelineEnabled.current = pipelineEnabled;
      return;
    }

    // Pipeline mode was toggled
    if (prevPipelineEnabled.current !== null && prevPipelineEnabled.current !== pipelineEnabled) {
      // Switch to appropriate home view
      setActiveView(homeView);
    }
    prevPipelineEnabled.current = pipelineEnabled;
  }, [pipelineEnabled, pipelineLoading, activeView, profile, homeView]);

  // QR pare-brise : un scan en attente ouvre l'écran conducteur sur ce véhicule, une seule
  // fois, après la vue initiale (sinon le choix pipeline/dashboard l'écraserait). La garde
  // d'affichage ci-dessous s'applique quand même : un profil sans `pool_driver` est renvoyé.
  useEffect(() => {
    if (!user || pipelineLoading || activeView === null) return;
    const token = takePendingScan();
    if (token) {
      setActiveView('pool_driver');
      setViewParams({ scanToken: token });
    }
  }, [user, pipelineLoading, activeView]);

  // Display-layer guard (H2): the sidebar filters by profile.modules server-side,
  // but a widget's onNavigate, Ctrl+K, a deep-link or a stale localStorage view can
  // still set `activeView` directly. Catch it here — the ONE place every view is
  // rendered — instead of re-checking in every caller. Server-side guards
  // (driver_read_guard, tenant isolation) remain the real authority on DATA; this is
  // display-only.
  useEffect(() => {
    if (!activeView || !profile) return;
    if (!isViewAllowed(activeView, profile, { isAdmin, isSuperuser, hasFeature })) {
      setActiveView(homeView);
      setViewParams({});
      toast.warning(t('viewAccess.restricted'));
    }
  }, [activeView, profile, isAdmin, isSuperuser, hasFeature, homeView, t, toast]);

  // Register global keyboard shortcuts
  useShortcut({
    key: 'h',
    ctrl: true,
    label: 'Accueil',
    category: 'Navigation',
    action: () => setActiveView(homeView)
  });

  useShortcut({
    key: '1',
    ctrl: true,
    label: 'Vehicules',
    category: 'Navigation',
    action: () => setActiveView('vehicles')
  });

  useShortcut({
    key: '2',
    ctrl: true,
    label: 'Conducteurs',
    category: 'Navigation',
    action: () => setActiveView('drivers')
  });

  useShortcut({
    key: '3',
    ctrl: true,
    label: 'Sinistres',
    category: 'Navigation',
    action: () => setActiveView('claims')
  });

  useShortcut({
    key: '4',
    ctrl: true,
    label: 'Maintenance',
    category: 'Navigation',
    action: () => setActiveView('maintenance')
  });

  useShortcut({
    key: 'Escape',
    ctrl: false,
    label: 'Fermer modal',
    category: 'Global',
    action: () => {
      // Close any open modals by clicking backdrop
      const backdrop = document.querySelector('[data-modal-backdrop]');
      if (backdrop) backdrop.click();
    }
  });

  // Check if onboarding is needed on mount
  useEffect(() => {
    const checkOnboarding = async () => {
      try {
        const response = await onboardingAPI.checkNeeded();
        if (response.ok && response.data.needed) {
          setShowOnboarding(true);
        }
      } catch (err) {
        // Onboarding check failed (may be expected)
      } finally {
        setOnboardingChecked(true);
      }
    };
    checkOnboarding();
  }, []);

  // Check if welcome modal should be shown (after onboarding check)
  useEffect(() => {
    if (!onboardingChecked || showOnboarding) {
      // Wait for onboarding check, skip if onboarding is showing
      return;
    }

    const checkWelcome = async () => {
      try {
        const response = await welcomeAPI.check();
        if (response.ok && response.data.show) {
          setShowWelcome(true);
        }
      } catch (err) {
        // Welcome check failed (may be expected)
      } finally {
        setWelcomeChecked(true);
      }
    };
    checkWelcome();
  }, [onboardingChecked, showOnboarding]);

  // Fetch real counts from APIs — only for modules the CURRENT profile can read.
  // Fixed 2026-09-13: this used to call all four endpoints unconditionally, so a
  // driver's boot fired vehicle/driver/claim/site getAll() straight into a 403
  // ROLE_READ_FORBIDDEN barrage. A profile without the module gets `null` counts
  // (badge simply doesn't render, see Sidebar's NavItem) instead of a failed fetch.
  const fetchCounts = useCallback(async () => {
    const canRead = {
      vehicles: Boolean(profile?.modules?.includes('vehicles')),
      drivers: Boolean(profile?.modules?.includes('drivers')),
      claims: Boolean(profile?.modules?.includes('claims')),
      sites: Boolean(profile?.modules?.includes('sites')),
    };

    if (!canRead.vehicles && !canRead.drivers && !canRead.claims && !canRead.sites) {
      setCounts({ vehicles: null, drivers: null, claims: null, sites: null });
      setCriticalAlerts(null);
      return;
    }

    // A 403 here is an EXPECTED outcome of a profile without the module, not a
    // technical failure — never console.error it, just fall back to "unknown".
    const safeFetch = (allowed, fn) => (allowed ? fn().catch(() => ({ ok: false, data: [] })) : Promise.resolve({ ok: false, data: [] }));

    const [vehiclesRes, driversRes, claimsRes, sitesRes] = await Promise.all([
      safeFetch(canRead.vehicles, () => vehicleAPI.getAll()),
      safeFetch(canRead.drivers, () => driverAPI.getAll()),
      safeFetch(canRead.claims, () => claimAPI.getAll()),
      safeFetch(canRead.sites, () => workingSiteAPI.getAll()),
    ]);

    const vehicles = vehiclesRes.ok ? vehiclesRes.data : [];
    const drivers = driversRes.ok ? driversRes.data : [];
    const claims = claimsRes.ok ? claimsRes.data : [];
    const sites = sitesRes.ok ? sitesRes.data : [];

    // Count active vehicles only (exclude retired/archived)
    const activeVehicles = vehicles.filter(v => v.status !== 'retired').length;

    // Count active drivers only (exclude inactive/terminated)
    const activeDrivers = drivers.filter(d => d.status !== 'inactive' && d.status !== 'terminated').length;

    // Count open claims (not closed or rejected)
    const openStatuses = ['draft', 'declared', 'acknowledged', 'expert_pending', 'expert_done', 'repair_authorized'];
    const openClaims = claims.filter(c => openStatuses.includes(c.status)).length;

    // Count active sites only
    const activeSites = sites.filter(s => s.status !== 'closed' && s.status !== 'inactive').length;

    setCounts({
      vehicles: canRead.vehicles ? activeVehicles : null,
      drivers: canRead.drivers ? activeDrivers : null,
      claims: canRead.claims ? openClaims : null,
      sites: canRead.sites ? activeSites : null,
    });

    // Calculate critical alerts (expired licenses, uninsured vehicles, etc.) — needs driver data
    if (canRead.drivers) {
      let criticalCount = 0;
      const today = new Date();
      drivers.forEach(d => {
        if (d.license_expiry && new Date(d.license_expiry) <= today) {
          criticalCount++;
        }
      });
      setCriticalAlerts(criticalCount);
    } else {
      setCriticalAlerts(null);
    }
  }, [profile]);

  useEffect(() => {
    fetchCounts();
    // Refresh counts every 5 minutes
    const interval = setInterval(fetchCounts, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchCounts]);

  // Navigate to a view, optionally with parameters
  const handleNavigate = useCallback((viewId, params = {}) => {
    // Display-layer guard (H2): a widget, Ctrl+K, or a deep-link can ask for any
    // view_code — refuse here, before it ever reaches renderView(), instead of
    // relying only on the redirect effect (which would flash the view for a frame).
    if (!isViewAllowed(viewId, profile, { isAdmin, isSuperuser, hasFeature })) {
      setViewParams({});
      setActiveView(homeView);
      toast.warning(t('viewAccess.restricted'));
      return;
    }
    // Clear params when navigating without explicit params
    setViewParams(params);
    setActiveView(viewId);
  }, [profile, isAdmin, isSuperuser, hasFeature, t, toast]);

  // Register navigation with Visual Help system for "Go there" functionality
  const { registerNavigate, updateCurrentView } = useVisualHelp();
  useEffect(() => {
    registerNavigate(handleNavigate);
  }, [registerNavigate, handleNavigate]);

  // Guided Experience - sync current view for spotlight targeting
  const { updateView: updateGuidedView } = useGuidedExperience();

  // Update current view in both Visual Help and Guided Experience contexts
  useEffect(() => {
    if (activeView) {
      updateCurrentView(activeView);
      updateGuidedView(activeView);
    }
  }, [activeView, updateCurrentView, updateGuidedView]);

  // Clear view params after they've been consumed by a view
  const clearViewParams = useCallback(() => {
    setViewParams({});
  }, []);

  const handleAction = (actionId, params = {}) => {
    // Handle quick actions + widget navigation with params
    // Quick action dispatched

    // Map quick action IDs to views
    const actionViewMap = {
      add_vehicle: 'vehicles',
      add_driver: 'drivers',
      view_sites: 'sites',
      add_fuel: 'fuel',
      add_maintenance: 'maintenance',
      declare_claim: 'claims',
      add_document: 'documents',
      run_report: 'reports',
      goto_pool: 'pool_driver'
    };

    // Valid view IDs for direct navigation
    const validViews = [
      'dashboard', 'pipeline', 'vehicles', 'drivers', 'sites', 'maintenance',
      'fuel', 'inspections', 'insurance', 'insurance-config', 'claims', 'fines',
      'tools', 'compliance', 'costs', 'suppliers', 'telematics', 'assignments',
      'cockpit', 'shares', 'risk-profiles', 'radar', 'emails', 'reports',
      'report-archive', 'documents', 'e_invoices', 'profile_admin', 'settings',
      'superadmin', 'billing', 'feature_monitor', 'sidebar-config', 'telematics_monitor',
      'pool_driver'
    ];

    if (actionViewMap[actionId]) {
      handleNavigate(actionViewMap[actionId], params);
    } else if (validViews.includes(actionId)) {
      handleNavigate(actionId, params);
    }
  };

  const renderView = () => {
    switch (activeView) {
      case 'dashboard':
        return <ErrorBoundary><Dashboard onAction={handleAction} /></ErrorBoundary>;

      case 'pipeline':
        return <V1Wrapper><PipelineHomeV2 onNavigate={handleNavigate} /></V1Wrapper>;

      case 'vehicles':
        return <V1Wrapper><VehiclesView returnTo={viewParams.returnTo} returnParams={viewParams.returnParams} onNavigate={handleNavigate} /></V1Wrapper>;

      case 'drivers':
        return <V1Wrapper><DriversView autoSelectId={viewParams.selectedId} returnTo={viewParams.returnTo} returnParams={viewParams.returnParams} onNavigate={handleNavigate} /></V1Wrapper>;

      case 'sites':
        return <V1Wrapper><SitesView /></V1Wrapper>;

      case 'pool_exceptions':
        return <V1Wrapper><PoolExceptionsView /></V1Wrapper>;

      case 'pool_driver':
        return <V1Wrapper><PoolDriverView scanToken={viewParams.scanToken || null} onScanDone={clearViewParams} /></V1Wrapper>;

      case 'maintenance':
        return <V1Wrapper><MaintenanceView autoSelectId={viewParams.selectedId || viewParams.selectMaintenanceId} createForVehicleId={viewParams.createForVehicleId} returnTo={viewParams.returnTo} onNavigate={handleNavigate} /></V1Wrapper>;

      case 'fuel':
        return <V1Wrapper><FuelView createForVehicleId={viewParams.createForVehicleId} defaultFuelType={viewParams.defaultFuelType} returnTo={viewParams.returnTo} onNavigate={handleNavigate} /></V1Wrapper>;

      case 'inspections':
        return <V1Wrapper><InspectionsView createForVehicleId={viewParams.createForVehicleId} returnTo={viewParams.returnTo} onNavigate={handleNavigate} /></V1Wrapper>;

      case 'insurance':
        return <V1Wrapper><InsuranceUserView initialTab={viewParams.tab} autoSelectClaimId={viewParams.selectedId} autoSelectContractId={viewParams.contractId} /></V1Wrapper>;

      case 'insurance-config':
        return <V1Wrapper><InsuranceConfigView /></V1Wrapper>;

      case 'claims':
        // Claims are now integrated into Insurance view - render insurance with claims tab active
        return <V1Wrapper><InsuranceUserView initialTab="claims" autoSelectClaimId={viewParams.selectedId || viewParams.selectClaimId} createForVehicleId={viewParams.createForVehicleId} /></V1Wrapper>;

      case 'fines':
        return <V1Wrapper><FinesView autoSelectId={viewParams.selectedId} createForVehicleId={viewParams.createForVehicleId} returnTo={viewParams.returnTo} onNavigate={handleNavigate} /></V1Wrapper>;

      case 'campaigns':
        return <V1Wrapper><CampaignsView /></V1Wrapper>;

      case 'tools':
        return <V1Wrapper><ToolsView /></V1Wrapper>;

      case 'compliance':
        return <V1Wrapper><GDPRComplianceView /></V1Wrapper>;

      case 'costs':
        return <V1Wrapper><CostDashboardView /></V1Wrapper>;

      case 'suppliers':
        return <V1Wrapper><FournisseursView /></V1Wrapper>;

      case 'telematics':
        return <ErrorBoundary><React.Suspense fallback={<div className="p-8 text-center text-gray-500">Chargement télématique...</div>}><TelematicsView /></React.Suspense></ErrorBoundary>;

      case 'assignments':
        return <V1Wrapper><AssignmentsView /></V1Wrapper>;

      case 'events':
      case 'calendar':
        return <V1Wrapper><CalendarView /></V1Wrapper>;

      case 'cockpit':
        return <V1Wrapper><FleetCockpitView /></V1Wrapper>;

      case 'shares':
        return <V1Wrapper><ShareList limit={50} /></V1Wrapper>;

      case 'risk-profiles':
        return <V1Wrapper><RiskProfilesView /></V1Wrapper>;

      case 'radar':
        return <V1Wrapper><AppRadarView /></V1Wrapper>;

      case 'emails':
        return <V1Wrapper><EmailTemplatesView /></V1Wrapper>;

      case 'data_export':
        return <V1Wrapper><DataExportView /></V1Wrapper>;

      case 'fiscal-co2':
        return <V1Wrapper><FiscalDashboardView /></V1Wrapper>;

      case 'budget':
        return <V1Wrapper><BudgetView /></V1Wrapper>;

      case 'reports':
        return <V1Wrapper><ReportsView /></V1Wrapper>;

      case 'report-archive':
        return <V1Wrapper><ReportArchiveView onNavigate={handleNavigate} /></V1Wrapper>;

      case 'documents':
        return <V1Wrapper><DocumentsView /></V1Wrapper>;

      case 'e_invoices':
        return (
          <V1Wrapper>
            <ErrorBoundary>
              <React.Suspense fallback={<div className="p-8 text-center text-gray-500">Chargement des factures électroniques…</div>}>
                <EInvoicesView />
              </React.Suspense>
            </ErrorBoundary>
          </V1Wrapper>
        );

      // V3 Components (no wrapper needed - already have their own layout)
      case 'profile_admin':
        return <ProfileAdminView initialTab={viewParams?.initialTab} />;

      case 'superadmin':
        return <ErrorBoundary><SuperAdminDashboard /></ErrorBoundary>;

      case 'billing':
        return (
          <ErrorBoundary><React.Suspense fallback={
            <div className="flex items-center justify-center h-screen">
              <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
            </div>
          }>
            <BillingDashboard />
          </React.Suspense></ErrorBoundary>
        );

      // V1 Components (wrapped for consistent padding)
      case 'settings':
        return <V1Wrapper><AdminView /></V1Wrapper>;

      case 'feature_monitor':
        return <V1Wrapper><FeatureMonitorTab /></V1Wrapper>;

      case 'telematics_monitor':
        return (
          <ErrorBoundary><React.Suspense fallback={
            <div className="flex items-center justify-center h-screen">
              <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
            </div>
          }>
            <TelematicsMonitorView />
          </React.Suspense></ErrorBoundary>
        );

      case 'sidebar-config':
        return <ProfileAdminView initialTab="sidebar-builder" />;

      // Driver Mono View (self-service for driver role)
      case 'driver-home':
        return <ErrorBoundary><DriverMonoView /></ErrorBoundary>;

      // Teams View (equipes - operational team management)
      case 'teams':
        return <V1Wrapper><TeamsView /></V1Wrapper>;

      // Mission Manager View (CRUD for managers/admins)
      case 'missions':
        return <V1Wrapper><MissionManagerView onNavigate={handleNavigate} /></V1Wrapper>;

      // Placeholder for remaining views
      default:
        return (
          <PlaceholderPage
            viewId={activeView}
            title={VIEW_LABELS[activeView] || activeView}
          />
        );
    }
  };

  // Handle onboarding completion
  const handleOnboardingComplete = (result) => {
    // Onboarding completed
    setShowOnboarding(false);
    // Refresh counts after onboarding
    fetchCounts();
  };

  // Handle onboarding skip
  const handleOnboardingSkip = () => {
    setShowOnboarding(false);
  };

  // Get tour controls and register navigation for tour
  const { startTour, registerNavigate: registerTourNavigate } = useTour();

  // Register navigation function for tour (so it can navigate to dashboard on start)
  React.useEffect(() => {
    if (registerTourNavigate) {
      registerTourNavigate(handleNavigate);
    }
  }, [registerTourNavigate, handleNavigate]);

  // Handle welcome modal close
  const handleWelcomeClose = () => {
    setShowWelcome(false);
  };

  // Handle welcome modal "Start Tour" button
  const handleWelcomeStartTour = () => {
    setShowWelcome(false);
    // Small delay to let modal close before starting tour
    setTimeout(() => startTour('main'), 300);
  };

  // Handle welcome modal "Configure Fleet" button (start onboarding)
  const handleWelcomeStartOnboarding = () => {
    setShowWelcome(false);
    setShowOnboarding(true);
  };

  // Show onboarding wizard if needed
  if (showOnboarding) {
    return (
      <OnboardingWizard
        onComplete={handleOnboardingComplete}
        onSkip={handleOnboardingSkip}
        tenant={tenant}
      />
    );
  }

  // Show loading while determining initial view
  if (activeView === null || pipelineLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-blue-500 animate-spin mx-auto mb-4" />
          <p className="text-gray-500">Chargement...</p>
        </div>
      </div>
    );
  }

  // Driver users now use normal Layout with the 'conducteur' profile
  // (profile system handles sidebar filtering — no more separate DriverLayout)

  return (
    <NavigationProvider onNavigate={handleNavigate}>
      <SearchProvider currentView={activeView}>
        {/* Welcome Modal - shows on first login or new version */}
        {showWelcome && (
          <WelcomeModal
            user={user}
            onClose={handleWelcomeClose}
            onStartTour={handleWelcomeStartTour}
            onStartOnboarding={handleWelcomeStartOnboarding}
            onNavigate={handleNavigate}
          />
        )}

        <Layout
          activeView={activeView}
          onNavigate={handleNavigate}
          counts={counts}
          criticalAlerts={criticalAlerts}
        >
          {/* Back to Pipeline floating bar */}
          {viewParams.returnTo === 'pipeline' && activeView !== 'pipeline' && (
            <div className="bg-slate-800 text-white px-4 py-2 flex items-center justify-between">
              <button
                onClick={() => handleNavigate('pipeline')}
                className="flex items-center gap-2 text-sm font-medium hover:text-blue-300 transition"
              >
                <ArrowLeft className="w-4 h-4" />
                Retour au Pipeline
              </button>
              <span className="flex items-center gap-1.5 text-xs text-slate-400">
                <ListChecks className="w-3.5 h-3.5" />
                Navigation depuis le Pipeline
              </span>
            </div>
          )}
          {renderView()}
        </Layout>
      </SearchProvider>
    </NavigationProvider>
  );
}

// Loading screen while checking authentication
function LoadingScreen() {
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="text-center">
        <Loader2 className="w-12 h-12 text-blue-500 animate-spin mx-auto mb-4" />
        <p className="text-gray-500">Chargement...</p>
      </div>
    </div>
  );
}

// Auth guard wrapper
function AuthenticatedApp() {
  const { isAuthenticated, isLoading, isOffboarded } = useAuth();

  // Show loading while checking auth
  if (isLoading) {
    return <LoadingScreen />;
  }

  // Show login page if not authenticated
  if (!isAuthenticated) {
    return <LoginPage />;
  }

  // Show archive view if authenticated but offboarded
  if (isOffboarded) {
    return <ArchiveView />;
  }

  // Show main app if authenticated (with theme support, tour, and visual help)
  return (
    <ThemeProvider>
      <TourProvider>
        <VisualHelpProvider>
          <GuidedExperienceProvider>
            <AppContent />
            <KeyboardShortcutsModal />
            <FloatingHelpPanel />
            <GuidedExperienceOverlay />
            <OnboardingChecklistCard />
          </GuidedExperienceProvider>
        </VisualHelpProvider>
      </TourProvider>
    </ThemeProvider>
  );
}

export function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <ProfileProvider>
          <PipelineProvider>
            <ToastProvider>
              <KeyboardShortcutsProvider>
                <AuthenticatedApp />
                <AutoResolveToast />
              </KeyboardShortcutsProvider>
            </ToastProvider>
          </PipelineProvider>
        </ProfileProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
